import * as Switch from '@radix-ui/react-switch'
import type { ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import { cn } from '@jotta/utils/css'
/**
 * Component to toggle something.
 *
 * Wraps a hidden checkbox which can be useful for making the component more accessible in the future
 */

function ButtonLabelWrapper({
  htmlFor,
  label,
  children,
  className,
}: {
  htmlFor: string
  label?: string
  children: ReactNode
  className?: string
}) {
  if (!label) {
    return <>{children}</>
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  )
}

export interface ToggleButtonProps extends Switch.SwitchProps {
  loading?: boolean
  disabled?: boolean
  className?: string
  label?: string
}
export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton(
    { checked, loading, disabled, label, className, children, ...props },
    ref,
  ) {
    const id = useId()
    const disabledOrLoading = disabled || loading
    return (
      <ButtonLabelWrapper htmlFor={id} label={label} className={className}>
        <Switch.Root
          checked={checked}
          className={cn('w-10 rounded-full bg-grey6 transition-colors', {
            'bg-input-control': checked,
            'opacity-25': disabledOrLoading,
          })}
          disabled={disabledOrLoading}
          ref={ref}
          id={id}
          {...props}
        >
          <Switch.Thumb
            className={cn(
              'block size-6 rounded-full border bg-grey3 transition-transform',
              {
                'translate-x-3/4 bg-grey1': checked,
              },
            )}
          />
        </Switch.Root>
        {children}
      </ButtonLabelWrapper>
    )
  },
)
